// Gerbang "toko ini dinonaktifkan pengelola platform" — keputusan + kalimatnya.
//
// KENAPA MODUL SENDIRI: keputusannya dipakai DUA permukaan yang sangat berbeda
// (storefront anon lewat root layout server, panel admin lewat sesi), dan
// kalimatnya BERBEDA di antara keduanya. Menaruhnya di komponen berarti dua
// salinan aturan yang pasti menyimpang; menaruhnya di `subscription.ts` berarti
// mencampur "apa yang boleh diakses" dengan aritmetika tanggal langganan.
//
// SUMBER FAKTA: `resolve_store_public.subscription_suspended` (mig 3850) =
// `store_subscriptions.manual_status = 'suspended'`, yaitu tombol **Nonaktifkan
// Langganan** di panel platform. SQL menyerahkan fakta; modul ini yang
// memutuskan artinya.
//
// LINGKUP YANG DISENGAJA — hanya penonaktifan MANUAL yang mengunci. Status
// `grace`/`expired` (turunan tanggal, dihitung `subscriptionStatus()` di
// `subscription.ts`) TETAP memakai perilaku lama: banner/popup yang bisa
// ditutup, toko tetap jalan. Alasannya: mengunci storefront adalah tindakan
// yang mematikan pendapatan tenant, dan itu harus selalu berupa keputusan
// manusia yang eksplisit — bukan akibat tanggal yang terlewat saat pemilik
// platform sedang cuti. Kalau kelak `expired` ingin ikut mengunci, ubah
// `storeAccessState()` DI SINI, bukan di komponen.

import type { SubscriptionStatus } from "@/lib/subscription";

/** Keadaan akses sebuah toko untuk pengunjungnya. */
export type StoreAccessState = "open" | "suspended";

/**
 * SATU tempat aturan "status langganan mana yang mengunci".
 *
 * Panel admin mengetahui statusnya lewat sesi (`store_subscription_self` →
 * `subscriptionStatus()`), storefront lewat boolean dari registry (mig 3850).
 * Dua jalur, satu kebijakan — dan kebijakan itu ditulis DI SINI supaya tak ada
 * permukaan yang diam-diam berbeda pendapat.
 *
 * Yang mengunci hanya `suspended` (penonaktifan manual pemilik platform).
 * `grace`/`expired` sengaja TIDAK: keduanya turunan tanggal, dan mengunci
 * pendapatan tenant karena kalender lewat sehari adalah kerusakan yang tidak
 * diminta siapa pun. Kalau kelak ingin diubah, ubah fungsi ini saja.
 */
export function subscriptionStatusBlocks(status: SubscriptionStatus): boolean {
  return status === "suspended";
}

/**
 * Bahan minimum penentu gerbang. Sengaja bukan `StoreContext` utuh supaya
 * modul ini tidak bergantung pada lapisan Supabase (dan bisa diuji murni).
 */
export type StoreAccessInput = {
  /** `subscription_suspended` dari registry. `undefined` = tidak diketahui. */
  suspended?: boolean | null;
  /**
   * Apakah registry benar-benar terjawab untuk request ini
   * (`StoreContext.reachable`). `false` = jangan menebak apa pun.
   */
  reachable?: boolean;
};

/**
 * Keadaan akses toko.
 *
 * **FAIL-OPEN, dan itu keputusan yang disengaja.** Nilai yang tidak diketahui —
 * kolom absen karena migrasi belum diterapkan, registry sedang tak terjawab,
 * bentuk data tak terduga — selalu berarti `"open"`.
 *
 * Arah gagalnya dipilih dari ongkosnya: mengunci storefront toko yang membayar
 * gara-gara satu RPC yang ngadat adalah kerusakan langsung pada pendapatan
 * orang lain, sementara terlambat mengunci toko yang menunggak hanya menunda
 * penagihan beberapa puluh detik. Jangan dibalik jadi fail-closed tanpa
 * membicarakannya lebih dulu.
 */
export function storeAccessState(input: StoreAccessInput): StoreAccessState {
  if (input.reachable === false) return "open";
  return input.suspended === true ? "suspended" : "open";
}

/** Ringkas: apakah permukaan pengunjung harus dikunci. */
export function isStoreSuspended(input: StoreAccessInput): boolean {
  return storeAccessState(input) === "suspended";
}

export type StoreSuspendedCopy = {
  title: string;
  body: string;
  /** Baris kecil di bawah body; null = tidak ada. */
  hint: string | null;
};

/**
 * Kalimat untuk PEMBELI (storefront).
 *
 * SENGAJA TIDAK menyebut langganan, tagihan, atau penonaktifan oleh pengelola
 * platform. Halaman ini dilihat pelanggan toko, dan memberi tahu mereka bahwa
 * tokonya menunggak adalah mempermalukan tenant di depan pasarnya sendiri —
 * kerugian yang tidak sebanding dengan tujuan penguncian. Yang perlu diketahui
 * pembeli hanyalah: sedang tutup, jangan mengirim uang.
 *
 * `storeName` kosong → kalimat generik, bukan "Toko  sedang…" dengan lubang.
 */
export function storeSuspendedCopyForBuyer(
  storeName?: string | null
): StoreSuspendedCopy {
  const name = (storeName ?? "").trim();
  return {
    title: name === "" ? "Toko sedang tidak aktif" : `${name} sedang tidak aktif`,
    body:
      "Toko ini sedang tidak menerima pesanan. Jangan kirim pembayaran apa pun selama pemberitahuan ini masih tampil.",
    hint: "Hubungi penjual lewat kontak yang biasa dipakai.",
  };
}

/**
 * Kalimat untuk ADMIN TOKO (panel admin).
 *
 * Kebalikan dari versi pembeli: di sini justru harus terus terang — pembacanya
 * adalah orang yang bisa menyelesaikannya, dan menyamarkan sebabnya hanya
 * membuat ia menebak-nebak kenapa tokonya mati.
 */
export function storeSuspendedCopyForAdmin(): StoreSuspendedCopy {
  return {
    title: "Toko Anda dinonaktifkan",
    body:
      "Pengelola platform menonaktifkan toko Anda. Selama status ini berlaku, storefront tidak melayani pesanan dan panel admin terkunci.",
    hint: "Buat tagihan perpanjangan dan kirim bukti transfernya di halaman Langganan Toko. Toko aktif kembali begitu pengelola platform menyetujuinya.",
  };
}

/** Halaman tempat toko nonaktif membayar tagihan perpanjangannya (mig 8170). */
export const SUSPENDED_PAY_PATH = "/admin/langganan";

/**
 * Apakah popup kunci toko nonaktif DILEWATI di path ini (putusan pemilik
 * 2026-10-22). Hanya halaman Langganan Toko: tanpa pengecualian ini toko yang
 * dinonaktifkan tak bisa membuka halaman tempat ia membayar — penguncian malah
 * menghalangi pembayaran yang jadi tujuannya. Sub-path ikut, path yang
 * kebetulan berawalan sama (`/admin/langganan-x`) tidak.
 */
export function adminPathSkipsSuspendLock(pathname: string | null | undefined): boolean {
  const p = (pathname ?? "").split("?")[0].replace(/\/+$/, "");
  return p === SUSPENDED_PAY_PATH || p.startsWith(`${SUSPENDED_PAY_PATH}/`);
}
